/**
 * Cache Manager Utility
 * Provides centralized cache clearing functionality for logout
 */

// Cache clearing functions registry
const cacheCleaners: Array<() => void> = [];

/**
 * Register a cache clearing function
 * @param cleaner - Function that clears specific caches
 */
export const registerCacheCleaner = (cleaner: () => void): void => {
  cacheCleaners.push(cleaner);
};

/**
 * Clear all registered caches
 * Called during logout to ensure no stale data persists
 */
export const clearAllRegisteredCaches = (): void => {
  cacheCleaners.forEach((cleaner, _index) => {
    try {
      cleaner();
    } catch (error) {
    }
  });
};

/**
 * Clear service-specific caches
 */
export const clearServiceCaches = async (): Promise<void> => {
  try {
    // Clear MobileCacheService cache
    try {
      const { mobileCacheService } = await import(
        "@shared/services/mobileCache.service"
      );
      await mobileCacheService.clearAll();
    } catch (error) {
    }

    // Clear OptimizedQueries cache
    try {
      const { clearAllCaches } = await import(
        "@shared/services/optimizedQueries.service"
      );
      clearAllCaches();
    } catch (error) {
    }
  } catch (error) {
  }
};

/**
 * Clear all browser storage and caches
 * Comprehensive cleanup for logout
 */
export const clearAllBrowserData = async (): Promise<void> => {
  try {
    // Clear localStorage items (from firebase-pwa.ts and other services)
    localStorage.removeItem("user_logged_in");
    localStorage.removeItem("user_email");
    localStorage.removeItem("user_display_name");
    localStorage.removeItem("user_id");
    localStorage.removeItem("userActivities");

    // Clear any other user-specific localStorage items
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        (key.startsWith("user_") ||
          key.includes("analytics") ||
          key.includes("bonds") ||
          key.includes("zurilink_subdomain"))
      ) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));

    // Clear sessionStorage cache
    const sessionKeysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith("zurilink_cache_")) {
        sessionKeysToRemove.push(key);
      }
    }
    sessionKeysToRemove.forEach((key) => sessionStorage.removeItem(key));

    // Clear IndexedDB data
    const { clearAllData } = await import("@shared/utils/indexedDB");
    await clearAllData();

    // Clear ONLY application-specific service worker caches (preserve browser image caches)
    if ("caches" in window) {
      const cacheNames = await caches.keys();
      const appSpecificCaches = cacheNames.filter((cacheName) => {
        const name = cacheName.toLowerCase();
        return (
          // Only clear our app's caches
          (name.includes("zurilink") ||
            name.includes("workbox") ||
            name.includes("static-resources") ||
            name.includes("fonts-cache") ||
            name.startsWith("sw-") ||
            name.includes("firebase") ||
            name.includes("runtime")) &&
          // NEVER clear anything that might contain images
          !name.includes("image") &&
          !name.includes("google") &&
          !name.includes("avatar") &&
          !name.includes("profile")
        );
      });

      // Add a small delay to avoid interfering with ongoing image loads
      await new Promise((resolve) => setTimeout(resolve, 100));

      await Promise.all(
        appSpecificCaches.map(async (cacheName) => {
          try {
            await caches.delete(cacheName);
          } catch (error) {
          }
        })
      );
    }

    // Clear subdomain service cache
    try {
      const { subdomainService } = await import("@/services/subdomain.service");
      subdomainService.clearCache();
    } catch (error) {
    }

    // Clear service-specific caches
    await clearServiceCaches();

    // Clear all registered application caches
    clearAllRegisteredCaches();
  } catch (error) {
    throw error;
  }
};
